import { useCallback, useEffect, useState } from "react";
import { PackageX, Plus } from "lucide-react";
import { toast } from "sonner";
import { LedgerList } from "./LedgerList";
import { WasteModal } from "./WasteModal";
import { fmtQty, invApi } from "./api";

const money = (n) => `$${(n || 0).toFixed(2)}`;

export function WasteView({ token }) {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [tossing, setTossing] = useState(false);

  const load = useCallback(() => {
    invApi(token).get("/inventory/waste", { params: { days } }).then((r) => setData(r.data)).catch(() => toast.error("Couldn't load waste log"));
  }, [token, days]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="waste-view">
      <div className="grid grid-cols-3 gap-2 sm:gap-3">
        {[["Tossed today", data ? money(data.today_cost) : "—", "stat-waste-today"], [`Last ${days} days`, data ? money(data.cost) : "—", "stat-waste-period"], ["Tosses", data?.count ?? "—", "stat-waste-count"]].map(([t, v, tid]) => (
          <div key={t} className="bg-white border border-stone-200 rounded-2xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-widest text-stone-500 font-bold">{t}</div>
            <div className={`text-2xl font-black tabular-nums ${tid === "stat-waste-today" && data?.today_cost > 0 ? "text-red-600" : "text-stone-900"}`} data-testid={tid}>{v}</div>
          </div>
        ))}
      </div>
      <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
        <div className="px-4 py-3 border-b border-stone-200 flex items-center gap-2 flex-wrap">
          <PackageX className="w-4 h-4 text-brand-red" />
          <div className="font-black text-stone-800 text-sm uppercase tracking-widest">Waste / shrink</div>
          <div className="ml-auto flex items-center gap-2">
            <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="waste-days-select" className="text-xs font-bold border border-stone-200 rounded-full px-2 py-1 bg-white">
              {[7, 30, 90].map((d) => <option key={d} value={d}>Last {d} days</option>)}
            </select>
            <button type="button" onClick={() => setTossing(true)} data-testid="waste-toss-btn" className="bg-brand-red text-white px-3 py-1.5 rounded-full text-[10px] uppercase tracking-widest font-black flex items-center gap-1"><Plus className="w-3.5 h-3.5" /> Toss it</button>
          </div>
        </div>
        {!data ? <div className="px-5 py-8 text-center text-sm text-stone-400">Loading…</div> : data.items.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-stone-400" data-testid="waste-empty">Nothing tossed in this period.</div>
        ) : (
          <table className="w-full text-sm" data-testid="waste-table">
            <thead className="text-[10px] uppercase tracking-widest text-stone-500 font-bold bg-stone-50">
              <tr><th className="text-left px-4 py-2">Ingredient</th><th className="text-right px-2 py-2">Tossed</th><th className="text-right px-4 py-2">Cost</th></tr>
            </thead>
            <tbody className="divide-y divide-stone-100">
              {data.items.map((i) => (
                <tr key={i.ingredient_id} data-testid={`waste-row-${i.ingredient_id}`}>
                  <td className="px-4 py-2.5 font-bold text-stone-800">{i.name}<span className="text-[11px] text-stone-400 font-normal"> · {i.count}×</span></td>
                  <td className="px-2 py-2.5 text-right tabular-nums">{fmtQty(i.qty)} <span className="text-[10px] text-stone-400 font-bold">{i.unit}</span></td>
                  <td className="px-4 py-2.5 text-right font-black tabular-nums text-red-600">{money(i.cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {data?.entries?.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden">
          <div className="px-5 py-3 border-b border-stone-200 font-black text-stone-800 text-sm uppercase tracking-widest">Toss log</div>
          <LedgerList token={token} rows={data.entries} onChanged={load} />
        </div>
      )}
      {tossing && <WasteModal token={token} onClose={() => setTossing(false)} onDone={() => { setTossing(false); load(); }} />}
    </div>
  );
}
